/**
 * Ocean / Sky / Star / Rainbow trail-world map config, extracted from Pixley-iOS:
 * - Learn/AlphabetTrailWorldComponents.swift (nodes + shared chrome)
 * - Learn/AlphabetAMCourseIntegration.swift (Ocean / Sky / Star)
 * - Learn/AlphabetVWXYCourseIntegration.swift (Rainbow U–Z)
 * - Learn/AlphabetWorldsCourseView.swift (unlock start indexes)
 * - Learn/AlphabetWorldCourseCatalog.swift (titles / letters / backgrounds)
 * - Learn/LearnPipCompanion.swift (LearnCompanionLayout.trailSize)
 *
 * Do not invent coordinates — values match AlphabetWorldTrailLayout exactly.
 * Garden World is separate (GardenWorldCourseView) and is not in TRAIL_WORLDS.
 */

/** Five-letter trail (Ocean F–J, Sky K–O, Star P–T). */
export const FIVE_LETTER_TRAIL_NODES = [
  { x: 0.31, y: 0.79 },
  { x: 0.68, y: 0.65 },
  { x: 0.39, y: 0.52 },
  { x: 0.68, y: 0.39 },
  { x: 0.47, y: 0.27 },
];

/** Six-letter trail (Rainbow U–Z). */
export const SIX_LETTER_TRAIL_NODES = [
  { x: 0.31, y: 0.81 },
  { x: 0.68, y: 0.70 },
  { x: 0.39, y: 0.59 },
  { x: 0.68, y: 0.47 },
  { x: 0.39, y: 0.35 },
  { x: 0.62, y: 0.24 },
];

/**
 * Shared header chrome from AlphabetTrailWorldChrome + AlphabetWorldChromeLayout.
 * Positions are fractions of the full-bleed map size unless noted.
 */
export const TRAIL_WORLD_CHROME = {
  /** AlphabetWorldChromeLayout.topOffset — tall portrait when height/width > 1.65 */
  tallPortraitAspectThreshold: 1.65,
  topOffsetTall: { widthFrac: 0.065, heightFrac: 0.03 },
  topOffsetShort: { widthFrac: 0.028, heightFrac: 0.016 },

  backButton: {
    x: 0.095,
    y: 0.058, // + topSafeShift
    sizeWidthFrac: 0.105,
    iconWidthFrac: 0.05,
  },

  title: {
    x: 0.5,
    y: 0.052, // + topSafeShift
    fontWidthFrac: 0.057,
    minimumScaleFactor: 0.78,
  },

  /** Progress capsule: "{completed} of {letters.length}" */
  progress: {
    x: 0.5,
    y: 0.096, // + topSafeShift
    widthFrac: 0.22,
    heightWidthFrac: 0.068,
    fontWidthFrac: 0.039,
  },

  /**
   * Pip / LearnCompanion (.standing) — top-right, top edge level with back button.
   * mascotSize = min(width * 0.26, 112)
   * chromeTop = height * 0.058 + topSafeShift - width * 0.0525
   * position.x = width - mascotSize * 0.5 - width * 0.03
   * position.y = chromeTop + mascotSize * 0.5
   */
  companion: {
    sizeWidthFrac: 0.26,
    sizeMaxPx: 112,
    trailingGutterWidthFrac: 0.03,
    chromeTopY: 0.058, // + topSafeShift - width * 0.0525
    chromeTopWidthNudge: 0.0525,
  },

  /** Letter node diameter: min(width * 0.18, 112); first node * 1.08 */
  node: {
    sizeWidthFrac: 0.18,
    sizeMaxPx: 112,
    firstNodeScale: 1.08,
  },
};

/**
 * Pip / header behavior (AlphabetTrailWorldChrome):
 * - One standing Pip companion top-right; not interactive (allowsHitTesting false).
 * - No bottom Start dock (unlike GardenWorldChrome / GardenWorldBottomDock).
 * - Progress is a centered capsule under the title, not letter pips.
 * - Letter nodes themselves show complete / current / locked states.
 * - Star World uses white titleColor; others use theme ink.
 */
export const TRAIL_WORLD_NOTES = {
  hasStartDock: false,
  gardenHasStartDock: true,
  companionRole: 'standing',
  progressStyle: 'countOfTotalCapsule',
  unlockGate: 'completedLetterCount >= world.startIndex',
};

export const TRAIL_WORLDS = {
  ocean: {
    id: 'ocean',
    route: '/learn/letters/ocean',
    title: 'Ocean World',
    letters: ['F', 'G', 'H', 'I', 'J'],
    startIndex: 5,
    background: 'ocean_world_screen_background',
    nodes: FIVE_LETTER_TRAIL_NODES,
    hasStartDock: false,
    motionStyle: 'bubbles',
    titleColor: null,
  },
  sky: {
    id: 'sky',
    route: '/learn/letters/sky',
    title: 'Sky World',
    letters: ['K', 'L', 'M', 'N', 'O'],
    startIndex: 10,
    background: 'sky_world_screen_background',
    nodes: FIVE_LETTER_TRAIL_NODES,
    hasStartDock: false,
    motionStyle: 'sparkles',
    titleColor: null,
  },
  star: {
    id: 'star',
    route: '/learn/letters/star',
    title: 'Star World',
    letters: ['P', 'Q', 'R', 'S', 'T'],
    startIndex: 15,
    background: 'star_world_screen_background',
    nodes: FIVE_LETTER_TRAIL_NODES,
    hasStartDock: false,
    motionStyle: 'sparkles',
    titleColor: '#ffffff',
  },
  rainbow: {
    id: 'rainbow',
    route: '/learn/letters/rainbow',
    title: 'Rainbow World',
    letters: ['U', 'V', 'W', 'X', 'Y', 'Z'],
    startIndex: 20,
    background: 'rainbow_world_screen_background',
    nodes: SIX_LETTER_TRAIL_NODES,
    hasStartDock: false,
    motionStyle: 'sparkles',
    titleColor: null,
  },
};

export const TRAIL_WORLD_LIST = [
  TRAIL_WORLDS.ocean,
  TRAIL_WORLDS.sky,
  TRAIL_WORLDS.star,
  TRAIL_WORLDS.rainbow,
];

export function trailWorldById(id) {
  return TRAIL_WORLDS[id] ?? null;
}

/** Match AlphabetWorldTrailLayout.normalizedPoints(for:) for 5 or 6 letters. */
export function trailNodesForLetterCount(letterCount) {
  if (letterCount === 6) return SIX_LETTER_TRAIL_NODES;
  if (letterCount === 5) return FIVE_LETTER_TRAIL_NODES;
  return FIVE_LETTER_TRAIL_NODES.slice(0, Math.max(0, letterCount));
}
